import StateObserver, { now, user } from 'src/StateObserver';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import * as Match2 from 'src/replicant/match2';
import {
  Match2PowerupType,
  PuzzleResults,
} from 'src/replicant/match2/match2.ruleset';
import { BlockId } from '../game/defs/block';
import { Match2ProgressDailyChallengeArgs } from 'src/replicant/match2/dailychallenge/match2-dailychallenge.actions';
import { openMatch2RewardsPopup } from '../rewards/match2-rewards.utils';
import { PuzzleStartData } from '../match2.analytics';
import { track } from 'src/lib/analytics/utils';
import { shouldSyncDailyChallenge } from 'src/replicant/match2/dailychallenge/match2-dailychallenge.getters';

export async function match2DailyChallengePostGame() {
  if (
    Match2.dailyChallengeGetters.isDailyChallengeAvailable(
      StateObserver.getState().user,
    ) &&
    Match2.dailyChallengeGetters.isDailyChallengeComplete(
      StateObserver.getState().user,
    ) &&
    !StateObserver.getState().user.match2.dailyChallenge?.rewardsClaimed
  ) {
    await openMatch2DailyChallengePopup('postPlay');
  }
}

export async function openMatch2DailyChallengePopup(
  originFeature: 'hud' | 'launch' | 'postPlay',
) {
  const origin = mapFeatureToDialogOrigin(originFeature);
  const popupId = 'popupMatch2DailyChallenge';
  track('DailyChallengeOpen', {
    popupId,
    popupOrigin: origin,
    originFeature,
  });
  const success = await openPopupPromise(popupId, {
    origin,
  });
  if (success) await StateObserver.invoke.claimMatch2DailyChallengeRewards();
}

export async function grantMatch2DailyChallengeRewards(
  originFeature: 'hud' | 'launch' | 'postPlay',
) {
  const config = Match2.dailyChallengeGetters.getDailyChallengeConfig(
    StateObserver.getState().user,
  );
  if (!config) return;

  track('DailyChallengeOpen', { originFeature });
  await openMatch2RewardsPopup({
    rewards: [{ type: 'gems', amount: config.rewardGems }],
    useSfx: true,
    origin: mapFeatureToDialogOrigin(originFeature),
  });
  await StateObserver.invoke.claimMatch2DailyChallengeRewards();
}

// FIXME: This should happen at the backend, currently it allows for cheating.
export async function matchDailyChallengeProgress(
  results: PuzzleResults,
  startData: PuzzleStartData,
) {
  // Skip if feature is now available
  if (!Match2.dailyChallengeGetters.isDailyChallengeAvailable(user())) {
    return undefined;
  }

  // Sync up daily challenge state, if required
  if (shouldSyncDailyChallenge(user(), now())) {
    await StateObserver.invoke.syncMatch2DailyChallenge();
  }

  // Skip if game was lost or quitted
  if (results.result !== 'win') return undefined;

  // Skip if user has claimed rewards already
  if (user().match2.dailyChallenge?.rewardsClaimed) return undefined;

  const progresses: Match2ProgressDailyChallengeArgs[] = [];

  // Send daily challenge updates for basic block destroy goals
  let id: BlockId;
  for (id in results.blocksBroken) {
    const amount = results.blocksBroken[id];
    if (!amount) continue;

    progresses.push({
      goal: {
        type: 'basicBlocks',
        subType: id,
      },
      amount,
    });
  }

  // Send daily challenge updates for power block creation goals
  let type: Match2PowerupType;
  for (type in results.powerBlocksCreated) {
    const amount = results.powerBlocksCreated[type];
    if (!amount) continue;

    progresses.push({
      goal: {
        type: 'powerBlocks',
        subType: type,
      },
      amount,
    });
  }

  // Send daily challenge update for moves remaining goals
  progresses.push({
    goal: { type: 'movesRemaining' },
    amount: results.moves.remaining,
  });

  // Send daily challenge update for level completion goals
  progresses.push({
    goal: { type: 'levelCompletes', subType: 'default' },
    amount: 1,
  });

  // For max buru streak variant, only send if streak is maxed at 3
  if (startData.buruStreakValue >= 3) {
    progresses.push({
      goal: { type: 'levelCompletes', subType: 'maxBuruStreak' },
      amount: 1,
    });
  }

  await StateObserver.invoke.triggerMatch2DailyChallengeUpdate(progresses);

  return progresses;
}
